import { useState, useContext, useCallback, useEffect, useRef } from "react";
import { Flex } from "uikit-react";
import { useForm } from "react-hook-form";
import moment from "moment";

import { AppContexts } from "../../../providers";
import { useDebounce } from "../../../hook";

const DATE_PRESETS = [
  { label: "Last 7 Days", value: "last-7" },
  { label: "Last 30 Days", value: "last-30" },
  { label: "Last 90 Days", value: "last-90" },
  { label: "This Week", value: "this-week" },
  { label: "Last Week", value: "last-week" },
  { label: "This Month", value: "this-month" },
  { label: "Last Month", value: "last-month" },
  { label: "This Quarter", value: "this-quarter" },
  { label: "Last Quarter", value: "last-quarter" },
  { label: "Year to Date", value: "ytd" },
  { label: "This Year", value: "this-year" },
  { label: "Last Year", value: "last-year" },
  { label: "Custom", value: "custom" },
];

const getPresetRange = (preset) => {
  const today = moment();
  switch (preset) {
    case "last-7":
      return [moment().subtract(7, "days"), today];
    case "last-30":
      return [moment().subtract(30, "days"), today];
    case "last-90":
      return [moment().subtract(90, "days"), today];
    case "this-week":
      return [moment().startOf("isoWeek"), today];
    case "last-week":
      return [
        moment().subtract(1, "week").startOf("isoWeek"),
        moment().subtract(1, "week").endOf("isoWeek"),
      ];
    case "this-month":
      return [moment().startOf("month"), today];
    case "last-month":
      return [
        moment().subtract(1, "month").startOf("month"),
        moment().subtract(1, "month").endOf("month"),
      ];
    case "this-quarter":
      return [moment().startOf("quarter"), today];
    case "last-quarter":
      return [
        moment().subtract(1, "quarter").startOf("quarter"),
        moment().subtract(1, "quarter").endOf("quarter"),
      ];
    case "ytd":
      return [moment().startOf("year"), today];
    case "this-year":
      return [moment().startOf("year"), today];
    case "last-year":
      return [
        moment().subtract(1, "year").startOf("year"),
        moment().subtract(1, "year").endOf("year"),
      ];
    default:
      return null;
  }
};

const FilterPanel = ({
  onSubmit = () => {},
  onFilterChange = () => {},
  searchTermPlaceholder,
}) => {
  const [filter, setFilter] = useState("");
  const [preset, setPreset] = useState("last-7");
  const ReportsContext = useContext(AppContexts.ReportsContext);
  const { register, handleSubmit, setValue, getValues } = useForm();
  const debouncedFilter = useDebounce(filter, 300);
  const skipPresetSync = useRef(false);
  const startField = register("start");
  const endField = register("end");

  const handleRegister = useCallback((data) => {
    ReportsContext.updateRange(data.start, data.end);
    onSubmit(data.start, data.end);
  }, []);

  const handleFilterChange = useCallback((e) => setFilter(e.target.value), []);

  const handlePresetChange = useCallback(
    (e) => {
      const value = e.target.value;
      setPreset(value);
      const range = getPresetRange(value);
      if (range) {
        const start = range[0].format("YYYY-MM-DD");
        const end = range[1].format("YYYY-MM-DD");
        setValue("start", start);
        setValue("end", end);
        skipPresetSync.current = true;
        ReportsContext.updateRange(start, end);
        onSubmit(start, end);
      }
    },
    [setValue, ReportsContext, onSubmit]
  );

  useEffect(
    () =>
      setValue("start", moment(ReportsContext.startDate).format("YYYY-MM-DD")),
    [ReportsContext.startDate]
  );
  useEffect(
    () => setValue("end", moment(ReportsContext.endDate).format("YYYY-MM-DD")),
    [ReportsContext.endDate]
  );
  useEffect(() => onFilterChange(debouncedFilter), [debouncedFilter]);

  // When context dates change externally, try to match a preset or fall back to "custom"
  useEffect(() => {
    if (skipPresetSync.current) {
      skipPresetSync.current = false;
      return;
    }
    const start = moment(ReportsContext.startDate);
    const end = moment(ReportsContext.endDate);
    const matched = DATE_PRESETS.find((p) => {
      if (p.value === "custom") return false;
      const range = getPresetRange(p.value);
      return (
        range &&
        range[0].format("YYYY-MM-DD") === start.format("YYYY-MM-DD") &&
        range[1].format("YYYY-MM-DD") === end.format("YYYY-MM-DD")
      );
    });
    setPreset(matched ? matched.value : "custom");
  }, [ReportsContext.startDate, ReportsContext.endDate]);

  return (
    <form onSubmit={handleSubmit(handleRegister)}>
      <div className="uk-flex uk-flex-between uk-margin-small-bottom">
        <div className="uk-flex uk-flex-middle">
          <div className="uk-form-controls">
            <select
              className="uk-select"
              value={preset}
              onChange={handlePresetChange}
            >
              {DATE_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-form-controls uk-margin-small-left">
            <div className="hms-date-picker">
              <input
                className="uk-input"
                type="date"
                max={moment().format("YYYY-MM-DD")}
                defaultValue={moment().subtract(7, "day").format("YYYY-MM-DD")}
                {...startField}
                onChange={(e) => {
                  startField.onChange(e);
                  setPreset("custom");
                }}
              />
            </div>
          </div>
          <div className="uk-form-controls uk-margin-small-left">
            <div className="hms-date-picker">
              <input
                className="uk-input"
                type="date"
                max={moment().format("YYYY-MM-DD")}
                defaultValue={moment().format("YYYY-MM-DD")}
                {...endField}
                onChange={(e) => {
                  endField.onChange(e);
                  setPreset("custom");
                }}
              />
            </div>
          </div>
          {preset === "custom" && (
            <div className="uk-margin-small-left">
              <button
                className="uk-button hms-btn hms-red-btn no-margin"
                type="submit"
              >
                Go
              </button>
            </div>
          )}
        </div>
        {searchTermPlaceholder ? (
          <div className="w-50 uk-margin-large-left">
            <div className="uk-search uk-search-default w-100">
              <span className="uk-search-icon-flip" uk-search-icon="true" />
              <input
                className="uk-search-input"
                type="search"
                value={filter}
                placeholder={searchTermPlaceholder}
                onChange={handleFilterChange}
              />
            </div>
          </div>
        ) : null}
      </div>
    </form>
  );
};

export default FilterPanel;
